import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import {
  ClientSearchFormValues,
  ClientSearchFormViolation,
} from 'types/clientSearchFormTypes';
import { useServices } from 'hooks/useServices';
import { SelectControlOption } from 'types/state';
import { SelectBox } from 'design/selectBox';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { Client } from 'types/clientTypes';
import { CLIENT_SEARCH__MIN_TEXT_LENGTH } from 'utils/clientSearchFormUtils';

type Props = {
  disabled?: boolean;
  values: ClientSearchFormValues;
  violations: ClientSearchFormViolation[];
  onTextChange(nextValue: string): void;
  onClientChange(nextValue: Client): void;
};

export const ClientSearchFieldset = (props: Props) => {
  const { disabled, values, violations, onTextChange, onClientChange } = props;

  const intl = useIntlDictionary();

  const { searchClients, getClient } = useServices();

  const [clients, setClients] = React.useState<Client[]>([]);

  const partnerCodeHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'TextIsEmpty':
          return intl.inputValidationRequired;
        case 'TextTooShort':
          return intl.inputValidationSearchTextTooShort;
        case 'ClientNotSelected':
          return intl.inputValidationClientNotSelected;
      }
    }
  }, [
    intl.inputValidationClientNotSelected,
    intl.inputValidationRequired,
    intl.inputValidationSearchTextTooShort,
    violations,
  ]);

  const clientOptions = React.useMemo<SelectControlOption<string>[]>(() => {
    return clients.map((client) => ({
      value: `${client.id}`,
      title: client.org.name || '-',
      description: normalizeProfileLike(client.person).toFullName(),
    }));
  }, [clients]);

  const handleClientSelection = React.useCallback(
    (value: string) => {
      const clientId = Number(value);

      getClient(clientId).then((client) => {
        onClientChange(client);

        setClients([]);
      });
    },
    [getClient, onClientChange]
  );

  React.useEffect(() => {
    if (values.text.length >= CLIENT_SEARCH__MIN_TEXT_LENGTH) {
      searchClients(values.text).then((clients) =>
        setClients(clients.slice(0, 5))
      );
    }
  }, [searchClients, values.text]);

  React.useEffect(() => {
    setClients([]);
  }, [values.text]); // WARNING: неявная зависимость

  return (
    <FloatingLabelComponent
      lifted={!!values.text}
      invalid={violations.length > 0}
      titleText={
        <TextComponent color="secondary" size="m">
          {intl.clientSearchTextPlaceholder}
        </TextComponent>
      }
      hintText={
        <TextComponent color="critical" size="s">
          {partnerCodeHint}
        </TextComponent>
      }
    >
      <TextInputControlComponent
        name="text"
        autoFocus
        value={values.text}
        disabled={disabled}
        invalid={violations.length > 0}
        onValueChange={onTextChange}
      />
      {clientOptions.length > 0 && (
        <SelectBox
          options={clientOptions}
          onSelectValue={handleClientSelection}
        />
      )}
    </FloatingLabelComponent>
  );
};
